import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CircleDetailDto, CreateCircleRequest } from '../types/community'
import {
  getCircle,
  createCircle,
  renameCircle,
  leaveCircle,
  createInvitation,
  requestToJoin,
  listCircles,
} from '../lib/api/circles'

export function useCircles() {
  return useQuery({ queryKey: ['community', 'circles'], queryFn: listCircles, retry: false })
}

/** Detail for one circle (roster stats or family table + capability matrix). */
export function useCircleDetail(id: string | undefined) {
  return useQuery<CircleDetailDto>({
    queryKey: ['community', 'circle', id],
    queryFn: () => getCircle(id as string),
    enabled: !!id,
    retry: false,
  })
}

/** Invalidate everything a circle mutation can affect (sidebar list + detail + /me). */
function useCircleInvalidation() {
  const qc = useQueryClient()
  return (id?: string) => {
    qc.invalidateQueries({ queryKey: ['community', 'circles'] })
    qc.invalidateQueries({ queryKey: ['community', 'me'] })
    if (id) qc.invalidateQueries({ queryKey: ['community', 'circle', id] })
  }
}

export function useCreateCircle() {
  const invalidate = useCircleInvalidation()
  return useMutation({
    mutationFn: (req: CreateCircleRequest) => createCircle(req),
    onSuccess: () => invalidate(),
  })
}

export function useRenameCircle(id: string) {
  const invalidate = useCircleInvalidation()
  return useMutation({
    mutationFn: (name: string) => renameCircle(id, name),
    onSuccess: () => invalidate(id),
  })
}

export function useLeaveCircle(id: string) {
  const invalidate = useCircleInvalidation()
  return useMutation({
    mutationFn: () => leaveCircle(id),
    onSuccess: () => invalidate(id),
  })
}

export function useCreateInvitation(id: string) {
  return useMutation({ mutationFn: () => createInvitation(id) })
}

export function useRequestToJoin(id: string) {
  return useMutation({ mutationFn: (message?: string) => requestToJoin(id, message) })
}
